// HelpSystem.js
// Contains: OnboardingDemo, FAQScreen, HelpSheet
// Used by Home.js via the ? icon in the header

import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const C = {
  bg:            '#0B1928',
  bgCard:        '#132030',
  bgDeep:        '#091520',
  border:        'rgba(255,255,255,0.08)',
  orange:        '#FF6B00',
  orangeDim:     'rgba(255,107,0,0.12)',
  orangeBorder:  'rgba(255,107,0,0.35)',
  textPrimary:   '#F0F4F8',
  textSecondary: '#8496A8',
  textMuted:     '#4A6A8A',
  green:         '#00C851',
  greenDim:      'rgba(0,200,81,0.10)',
  greenBorder:   'rgba(0,200,81,0.28)',
};
const font = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

// ── ONBOARDING DEMO ───────────────────────────────────────────────────────────
const DEMO_SLIDES = [
  {
    icon: '🏆',
    title: 'A new bracket every day',
    body: 'Every day at midnight a fresh 32-entry bracket drops — sports, music, movies, TV, food and more. Your job: pick a champion.',
    color: '#FF6B00',
  },
  {
    icon: '👆',
    title: 'Pick your way to the top',
    body: 'Tap to pick a winner in each matchup. Your picks advance through 5 rounds — Round of 32 all the way to the Championship.',
    color: '#3B82F6',
    demo: 'bracket',
  },
  {
    icon: '📊',
    title: 'See how the world voted',
    body: 'After you submit, see exactly how every other player picked — matchup by matchup. Are you with the crowd or a contrarian?',
    color: '#00C851',
  },
  {
    icon: '🔥',
    title: 'Build your streak',
    body: 'Play every day to keep your streak alive. Hit milestones at 7, 30, 100 days. Miss a day and it resets — so don\'t forget!',
    color: '#F59E0B',
  },
  {
    icon: '🏅',
    title: 'Earn badges & chase the Hall of Fame',
    body: 'Earn 19 badges for consistency, accuracy, and social play. The first 10 players to earn all 16 challenge badges get Pro for life.',
    color: '#8B5CF6',
  },
];

export function OnboardingDemo({ onDone, fromFAQ = false }) {
  const [slide, setSlide]   = useState(0);
  const [exiting, setExiting] = useState(false);
  const current = DEMO_SLIDES[slide];

  // Demo bracket picks for slide 2
  const [demoPick, setDemoPick] = useState(null);

  const handleNext = () => {
    if (slide < DEMO_SLIDES.length - 1) {
      setSlide(s => s + 1);
      setDemoPick(null);
    } else {
      handleDone();
    }
  };

  const handleDone = () => {
    setExiting(true);
    setTimeout(() => {
      if (!fromFAQ) localStorage.setItem('bracketz_onboarding_done', '1');
      onDone();
    }, 300);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      backgroundColor: 'rgba(0,0,0,0.95)',
      display: 'flex', flexDirection: 'column',
      fontFamily: font,
      opacity: exiting ? 0 : 1,
      transition: 'opacity 0.3s',
    }}>
      <style>{`
        @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes demoPickPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.03)} }
      `}</style>

      {/* Skip button */}
      <div style={{ display:'flex', justifyContent:'flex-end', padding:'16px 20px 0' }}>
        <div onClick={handleDone} style={{ fontSize:'13px', color:C.textMuted, cursor:'pointer', padding:'6px 12px', border:`0.5px solid ${C.border}`, borderRadius:'20px' }}>
          Skip
        </div>
      </div>

      {/* Progress dots */}
      <div style={{ display:'flex', justifyContent:'center', gap:'6px', padding:'16px 0 0' }}>
        {DEMO_SLIDES.map((_, i) => (
          <div key={i} style={{ width: i === slide ? '20px' : '6px', height:'6px', borderRadius:'3px', backgroundColor: i === slide ? current.color : 'rgba(255,255,255,0.15)', transition:'all 0.3s' }}/>
        ))}
      </div>

      {/* Slide content */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px 32px', textAlign:'center', animation:'slideUp 0.3s ease' }} key={slide}>

        {/* Icon */}
        <div style={{ fontSize:'72px', marginBottom:'20px', filter:`drop-shadow(0 0 20px ${current.color}44)` }}>
          {current.icon}
        </div>

        {/* Title */}
        <div style={{ fontSize:'24px', fontWeight:'800', color:C.textPrimary, marginBottom:'12px', lineHeight:'1.2' }}>
          {current.title}
        </div>

        {/* Body */}
        <div style={{ fontSize:'14px', color:C.textSecondary, lineHeight:'1.7', marginBottom:'32px', maxWidth:'320px' }}>
          {current.body}
        </div>

        {/* Demo bracket for slide 2 */}
        {current.demo === 'bracket' && (
          <div style={{ width:'100%', maxWidth:'300px', marginBottom:'24px' }}>
            {[
              { t1:'Michael Jordan', t2:'LeBron James' },
              { t1:'Kobe Bryant', t2:'Magic Johnson' },
            ].map((match, idx) => (
              <div key={idx} style={{ backgroundColor:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'10px', overflow:'hidden', marginBottom:'10px' }}>
                {[match.t1, match.t2].map((team, ti) => (
                  <div key={ti} onClick={() => setDemoPick(`${idx}-${ti}`)}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', backgroundColor: demoPick===`${idx}-${ti}`?'rgba(255,107,0,0.1)':'transparent', borderBottom: ti===0?`0.5px solid ${C.border}`:'none', cursor:'pointer', transition:'background 0.15s', animation: demoPick===`${idx}-${ti}` ? 'demoPickPulse 0.4s ease' : 'none' }}>
                    <span style={{ fontSize:'13px', fontWeight: demoPick===`${idx}-${ti}`?'700':'400', color: demoPick===`${idx}-${ti}`?C.textPrimary:C.textSecondary }}>{team}</span>
                    <div style={{ width:'18px', height:'18px', borderRadius:'50%', backgroundColor: demoPick===`${idx}-${ti}`?C.orange:'transparent', border: demoPick===`${idx}-${ti}`?'none':`1.5px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}>
                      {demoPick===`${idx}-${ti}` && <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M2 4.5l2 2L7 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ fontSize:'11px', color:C.textMuted, textAlign:'center' }}>👆 Tap to pick a winner</div>
          </div>
        )}
      </div>

      {/* Next / Get Started button */}
      <div style={{ padding:'0 24px 48px' }}>
        <button onClick={handleNext} style={{ width:'100%', backgroundColor:current.color, color:'#fff', border:'none', borderRadius:'14px', padding:'16px', fontSize:'16px', fontWeight:'800', fontFamily:font, cursor:'pointer' }}>
          {slide < DEMO_SLIDES.length - 1 ? 'Next →' : "Let's Play! 🏆"}
        </button>
      </div>
    </div>
  );
}

// ── FAQ SCREEN ────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: 'How does Bracketz work?',
    a: 'Every day at midnight a new 32-entry bracket drops. Pick a winner in each matchup across 5 rounds — Round of 32, Round of 16, Elite 8, Final 4, and Championship. Submit your picks and see how you compare to everyone else.',
  },
  {
    q: 'How do streaks work?',
    a: 'Submit a bracket every day to build your streak. Miss a day and your streak resets to zero. Pro members get one Streak Freeze per month — use it to protect your streak if you know you\'ll miss a day.',
  },
  {
    q: 'What categories are there?',
    a: 'Sports, Music, Movies, TV, Food, Gaming, History, and Pop Culture. A new bracket drops daily and rotates through all categories.',
  },
  {
    q: 'What are badges?',
    a: 'Badges are earned by completing challenges — playing your first bracket, building streaks, posting comments, exploring categories, and more. There are 19 badges total across 4 rarity tiers: Common, Rare, and Legendary.',
  },
  {
    q: 'What is The Bracketz 10?',
    a: 'The Hall of Fame challenge. The first 10 players to earn all 16 qualifying badges (Oracle and Legend excluded) win Pro for life and a spot on the Hall of Fame leaderboard. Requires 100+ days of daily play minimum.',
  },
  {
    q: 'What does Pro include?',
    a: 'Bracketz Pro ($2.99/mo or $24.99/yr) includes: full crowd analytics showing every matchup pick rate, one Streak Freeze per month, early access to tomorrow\'s category at 9pm, and exclusive Pro-only brackets.',
  },
  {
    q: 'Can I gift Pro to a friend?',
    a: 'Yes! Go to Profile → Upgrade → Gift Pro. Enter your friend\'s email, pay $2.99, and they get 1 month of Pro instantly. You\'ll earn the Generous badge and a 10% discount on your next month.',
  },
  {
    q: 'How does sharing work?',
    a: 'After submitting, tap "Share Your Bracket" to generate a custom bracket card you can post to any social platform. You can also record your screen while picking for TikTok/Reels content.',
  },
  {
    q: 'What happens if I miss a day?',
    a: 'Your streak resets. But your total bracket count, badges, and history all remain. Pro members can use a Streak Freeze once per month to protect against one missed day.',
  },
  {
    q: 'How do I invite friends?',
    a: 'Go to the Friends tab and tap Invite. Enter their email and send — they\'ll get an invite email. When they join, you\'ll be notified and earn progress toward the referral badges.',
  },
];

export function FAQScreen({ onClose, onShowDemo }) {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div style={{ position:'fixed', inset:0, zIndex:2500, backgroundColor:C.bg, fontFamily:font, overflowY:'auto' }}>

      {/* Header */}
      <div style={{ backgroundColor:C.bg, padding:'14px 18px', borderBottom:`0.5px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ fontSize:'16px', fontWeight:'800', color:C.textPrimary }}>Help & FAQ</div>
        <div onClick={onClose} style={{ fontSize:'22px', color:C.textMuted, cursor:'pointer', lineHeight:1 }}>✕</div>
      </div>

      <div style={{ padding:'16px', paddingBottom:'48px' }}>

        {/* Watch the demo */}
        <div onClick={onShowDemo} style={{ backgroundColor:C.orangeDim, border:`1px solid ${C.orangeBorder}`, borderRadius:'14px', padding:'16px', marginBottom:'20px', cursor:'pointer', display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontSize:'32px', flexShrink:0 }}>▶️</div>
          <div>
            <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'3px' }}>Watch the app demo</div>
            <div style={{ fontSize:'12px', color:C.textSecondary }}>A quick 5-slide walkthrough of how Bracketz works</div>
          </div>
          <div style={{ marginLeft:'auto', color:C.orange, fontSize:'18px' }}>›</div>
        </div>

        {/* FAQ items */}
        <div style={{ fontSize:'9px', fontWeight:'700', color:C.textMuted, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'12px' }}>
          Frequently Asked Questions
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'24px' }}>
          {FAQ_ITEMS.map((item, idx) => (
            <div key={idx} style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`0.5px solid ${openIdx===idx?C.orangeBorder:C.border}`, overflow:'hidden', transition:'border-color 0.15s' }}>
              <div onClick={() => setOpenIdx(openIdx===idx ? null : idx)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', cursor:'pointer' }}>
                <div style={{ fontSize:'13px', fontWeight:'600', color:C.textPrimary, flex:1, paddingRight:'12px', lineHeight:'1.4' }}>{item.q}</div>
                <div style={{ fontSize:'18px', color:C.textMuted, flexShrink:0, transform:openIdx===idx?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}>▾</div>
              </div>
              {openIdx === idx && (
                <div style={{ padding:'0 16px 14px', fontSize:'13px', color:C.textSecondary, lineHeight:'1.7', borderTop:`0.5px solid ${C.border}`, paddingTop:'12px' }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Send feedback */}
        <FeedbackForm />
      </div>
    </div>
  );
}

function FeedbackForm() {
  const [type,    setType]    = useState('suggestion');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        type, message: message.trim(),
        createdAt: serverTimestamp(),
      });
      setSent(true);
      setMessage('');
    } catch(e) { console.error(e); }
    setSending(false);
  };

  return (
    <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`0.5px solid ${C.border}`, padding:'16px' }}>
      <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'12px' }}>Send Feedback</div>

      {sent ? (
        <div style={{ textAlign:'center', padding:'16px' }}>
          <div style={{ fontSize:'32px', marginBottom:'8px' }}>✅</div>
          <div style={{ fontSize:'14px', fontWeight:'700', color:C.green }}>Thanks for the feedback!</div>
          <div style={{ fontSize:'12px', color:C.textMuted, marginTop:'4px' }}>We read every submission.</div>
        </div>
      ) : (
        <>
          <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
            {['suggestion','bug','other'].map(t => (
              <div key={t} onClick={() => setType(t)} style={{ flex:1, padding:'6px', textAlign:'center', fontSize:'10px', fontWeight:'700', borderRadius:'8px', cursor:'pointer', backgroundColor:type===t?C.orange:'transparent', color:type===t?'#fff':C.textMuted, border:`0.5px solid ${type===t?C.orange:'rgba(255,255,255,0.1)'}`, textTransform:'capitalize' }}>
                {t}
              </div>
            ))}
          </div>
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Tell us what's on your mind..." rows={4}
            style={{ width:'100%', backgroundColor:'rgba(0,0,0,0.2)', border:`0.5px solid ${C.border}`, borderRadius:'8px', padding:'10px 12px', fontSize:'13px', color:C.textPrimary, fontFamily:font, outline:'none', boxSizing:'border-box', resize:'none', lineHeight:'1.5', marginBottom:'10px' }}/>
          <button onClick={handleSend} disabled={sending||!message.trim()} style={{ width:'100%', backgroundColor:sending||!message.trim()?'rgba(255,107,0,0.3)':C.orange, color:'#fff', border:'none', borderRadius:'10px', padding:'13px', fontSize:'13px', fontWeight:'700', fontFamily:font, cursor:sending||!message.trim()?'not-allowed':'pointer' }}>
            {sending ? 'Sending...' : 'Send Feedback'}
          </button>
        </>
      )}
    </div>
  );
}

// ── HELP SHEET — bottom sheet with FAQ + Demo options ────────────────────────
export function HelpSheet({ onClose, onShowFAQ, onShowDemo }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:2000, backgroundColor:'rgba(0,0,0,0.7)', display:'flex', alignItems:'flex-end', justifyContent:'center', fontFamily:font }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor:C.bgCard, borderRadius:'20px 20px 0 0', padding:'24px 20px 40px', width:'100%', maxWidth:'480px' }}>
        <div style={{ width:'36px', height:'4px', borderRadius:'2px', background:'rgba(255,255,255,0.15)', margin:'0 auto 20px' }}/>
        <div style={{ fontSize:'16px', fontWeight:'800', color:C.textPrimary, marginBottom:'16px' }}>Help</div>
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          <div onClick={onShowDemo} style={{ display:'flex', alignItems:'center', gap:'14px', backgroundColor:C.orangeDim, border:`1px solid ${C.orangeBorder}`, borderRadius:'14px', padding:'16px', cursor:'pointer' }}>
            <span style={{ fontSize:'24px' }}>▶️</span>
            <div>
              <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary }}>Watch the demo</div>
              <div style={{ fontSize:'12px', color:C.textSecondary, marginTop:'2px' }}>5-slide walkthrough of how the app works</div>
            </div>
          </div>
          <div onClick={onShowFAQ} style={{ display:'flex', alignItems:'center', gap:'14px', backgroundColor:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'14px', padding:'16px', cursor:'pointer' }}>
            <span style={{ fontSize:'24px' }}>❓</span>
            <div>
              <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary }}>FAQ & How to play</div>
              <div style={{ fontSize:'12px', color:C.textSecondary, marginTop:'2px' }}>Answers to common questions + send feedback</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
